/**
 * Editable form model for a sell-side rate card (strings while editing,
 * numbers on save) plus the client-side rules the server also enforces:
 * scope ↔ target, default mix sums to 100, added-value bonus 0–100%.
 */

import type {
  MediaPlanRateCard,
  MediaPlanUnitKey,
  RateCardInput,
  RateCardScope,
  RateCardStatus,
  RateCardUpcharge,
  TargetingLayerKey,
} from '../MediaPlans/types';
import { MEDIA_PLAN_UNIT_ORDER } from '../MediaPlans/types';

export interface UnitPriceDraft {
  cpm: string;
  flatUsd: string;
  flatPer: '' | 'week' | 'flight';
  floorCpm: string;
  note: string;
}

export interface UpchargeDraft {
  id: string;
  layer: TargetingLayerKey;
  label: string;
  cpmAdd: string;
  appliesTo: MediaPlanUnitKey[];
  freeWhenAgeOnly: boolean;
  note: string;
}

export interface CustomItemDraft {
  id: string;
  minBudgetUsd: string;
  label: string;
  valueUsd: string;
}

export interface RateCardDraft {
  id?: string;
  name: string;
  scope: RateCardScope;
  categoryKey: string;
  agencyKey: string;
  advertiserKey: string;
  status: RateCardStatus;
  effectiveFrom: string;
  effectiveTo: string;
  units: Record<MediaPlanUnitKey, UnitPriceDraft>;
  upcharges: UpchargeDraft[];
  hasAddedValue: boolean;
  bonusPct: string;
  bonusUnits: MediaPlanUnitKey[];
  customItems: CustomItemDraft[];
  addedValueNotes: string;
  hasDefaultMix: boolean;
  defaultUnitMix: Partial<Record<MediaPlanUnitKey, string>>;
  minimumBudgetUsd: string;
  notes: string;
}

let seq = 0;
export const nextId = (prefix: string): string => `${prefix}-${Date.now().toString(36)}-${(seq++).toString(36)}`;

export const emptyUnitPrice = (): UnitPriceDraft => ({ cpm: '', flatUsd: '', flatPer: '', floorCpm: '', note: '' });

export function emptyUnits(): Record<MediaPlanUnitKey, UnitPriceDraft> {
  return Object.fromEntries(MEDIA_PLAN_UNIT_ORDER.map((k) => [k, emptyUnitPrice()])) as Record<MediaPlanUnitKey, UnitPriceDraft>;
}

export function newRateCardDraft(): RateCardDraft {
  return {
    name: '',
    scope: 'category',
    categoryKey: '',
    agencyKey: '',
    advertiserKey: '',
    status: 'draft',
    effectiveFrom: new Date().toISOString().slice(0, 10),
    effectiveTo: '',
    units: emptyUnits(),
    upcharges: [],
    hasAddedValue: false,
    bonusPct: '10',
    bonusUnits: ['ns-15s'],
    customItems: [],
    addedValueNotes: '',
    hasDefaultMix: false,
    defaultUnitMix: {},
    minimumBudgetUsd: '',
    notes: '',
  };
}

const str = (n: number | undefined): string => (typeof n === 'number' && Number.isFinite(n) ? String(n) : '');

export function draftFromRateCard(card: MediaPlanRateCard, opts: { duplicate?: boolean } = {}): RateCardDraft {
  const units = emptyUnits();
  for (const k of MEDIA_PLAN_UNIT_ORDER) {
    const p = card.units[k];
    if (!p) continue;
    units[k] = { cpm: str(p.cpm), flatUsd: str(p.flatUsd), flatPer: p.flatPer || '', floorCpm: str(p.floorCpm), note: p.note || '' };
  }
  const mix: Partial<Record<MediaPlanUnitKey, string>> = {};
  for (const [k, v] of Object.entries(card.defaultUnitMix || {})) if (typeof v === 'number') mix[k as MediaPlanUnitKey] = String(v);
  return {
    id: opts.duplicate ? undefined : card.id,
    name: opts.duplicate ? `${card.name} (copy)` : card.name,
    scope: card.scope,
    categoryKey: card.categoryKey || '',
    agencyKey: card.agencyKey || '',
    advertiserKey: card.advertiserKey || '',
    status: opts.duplicate ? 'draft' : card.status,
    effectiveFrom: card.effectiveFrom || '',
    effectiveTo: card.effectiveTo || '',
    units,
    upcharges: (card.upcharges || []).map((u) => ({
      id: nextId('up'),
      layer: u.layer,
      label: u.label,
      cpmAdd: str(u.cpmAdd),
      appliesTo: u.appliesTo ? [...u.appliesTo] : [],
      freeWhenAgeOnly: u.freeWhen === 'age-only',
      note: u.note || '',
    })),
    hasAddedValue: !!card.addedValue,
    bonusPct: card.addedValue ? String(Math.round(card.addedValue.bonusPct * 10000) / 100) : '10',
    bonusUnits: card.addedValue ? [...card.addedValue.bonusUnits] : ['ns-15s'],
    customItems: (card.addedValue?.customItems || []).map((c) => ({ id: nextId('ci'), minBudgetUsd: str(c.minBudgetUsd), label: c.label, valueUsd: str(c.valueUsd) })),
    addedValueNotes: card.addedValue?.notes || '',
    hasDefaultMix: Object.keys(mix).length > 0,
    defaultUnitMix: mix,
    minimumBudgetUsd: str(card.minimums?.budgetUsd),
    notes: card.notes || '',
  };
}

export function parseNum(s: string): number | null {
  const t = s.replace(/[$,%\s]/g, '');
  if (!t) return null;
  const n = Number(t);
  return Number.isFinite(n) ? n : null;
}

export function mixTotal(mix: Partial<Record<MediaPlanUnitKey, string>>): number {
  return Object.values(mix).reduce<number>((sum, v) => sum + (parseNum(v || '') || 0), 0);
}

export function validateRateCardDraft(d: RateCardDraft): string[] {
  const errors: string[] = [];
  if (!d.name.trim()) errors.push('Give the rate card a name.');
  if (d.scope === 'category' && !d.categoryKey) errors.push('A category card needs a category.');
  if (d.scope === 'agency' && !d.agencyKey) errors.push('An agency card needs an agency.');
  if (d.scope === 'advertiser' && !d.advertiserKey) errors.push('An advertiser card needs an advertiser.');
  if (!/^\d{4}-\d{2}-\d{2}$/.test(d.effectiveFrom)) errors.push('Effective-from must be a date.');
  if (d.effectiveTo && d.effectiveTo < d.effectiveFrom) errors.push('Effective-to must be on or after effective-from.');

  let priced = 0;
  for (const k of MEDIA_PLAN_UNIT_ORDER) {
    const u = d.units[k];
    const cpm = parseNum(u.cpm);
    const flat = parseNum(u.flatUsd);
    const floor = parseNum(u.floorCpm);
    if (u.cpm.trim() && (cpm === null || cpm <= 0)) errors.push(`${k}: CPM must be a positive number.`);
    if (u.flatUsd.trim() && (flat === null || flat <= 0)) errors.push(`${k}: flat rate must be a positive number.`);
    if (u.floorCpm.trim() && (floor === null || floor < 0)) errors.push(`${k}: floor must be a number.`);
    if (flat !== null && flat > 0 && !u.flatPer) errors.push(`${k}: say whether the flat rate is per week or per flight.`);
    if ((cpm ?? 0) > 0 || (flat ?? 0) > 0) priced++;
  }
  if (priced === 0) errors.push('Price at least one ad unit.');

  d.upcharges.forEach((u, i) => {
    if (!u.label.trim()) errors.push(`Upcharge ${i + 1}: needs a label.`);
    const add = parseNum(u.cpmAdd);
    if (add === null || add < 0) errors.push(`Upcharge ${i + 1}: +CPM must be a number (0 or more).`);
  });

  if (d.hasAddedValue) {
    const pct = parseNum(d.bonusPct);
    if (pct === null || pct < 0 || pct > 100) errors.push('Bonus impressions must be between 0 and 100%.');
    if (d.bonusUnits.length === 0) errors.push('Pick at least one ad unit the bonus media runs on.');
    d.customItems.forEach((c, i) => {
      if (!c.label.trim()) errors.push(`Added-value item ${i + 1}: needs a label.`);
      const min = parseNum(c.minBudgetUsd);
      if (min === null || min <= 0) errors.push(`Added-value item ${i + 1}: minimum budget must be a positive number.`);
    });
  }

  if (d.hasDefaultMix) {
    const total = mixTotal(d.defaultUnitMix);
    if (Math.abs(total - 100) > 0.01) errors.push(`Default unit mix must sum to 100% (currently ${Math.round(total * 100) / 100}%).`);
  }
  const min = parseNum(d.minimumBudgetUsd);
  if (d.minimumBudgetUsd.trim() && (min === null || min <= 0)) errors.push('Minimum budget must be a positive number.');
  return errors;
}

export function draftToRateCardInput(d: RateCardDraft): RateCardInput {
  const units: RateCardInput['units'] = {};
  for (const k of MEDIA_PLAN_UNIT_ORDER) {
    const u = d.units[k];
    const cpm = parseNum(u.cpm);
    const flat = parseNum(u.flatUsd);
    const floor = parseNum(u.floorCpm);
    if (!((cpm ?? 0) > 0 || (flat ?? 0) > 0)) continue;
    units[k] = {
      ...(cpm !== null && cpm > 0 ? { cpm } : {}),
      ...(flat !== null && flat > 0 ? { flatUsd: flat, flatPer: (u.flatPer || 'flight') as 'week' | 'flight' } : {}),
      ...(floor !== null && floor >= 0 && u.floorCpm.trim() ? { floorCpm: floor } : {}),
      ...(u.note.trim() ? { note: u.note.trim() } : {}),
    };
  }
  const upcharges: RateCardUpcharge[] = d.upcharges.map((u) => ({
    layer: u.layer,
    label: u.label.trim(),
    cpmAdd: parseNum(u.cpmAdd) || 0,
    ...(u.appliesTo.length ? { appliesTo: [...u.appliesTo] } : {}),
    ...(u.freeWhenAgeOnly ? { freeWhen: 'age-only' as const } : {}),
    ...(u.note.trim() ? { note: u.note.trim() } : {}),
  }));
  const mix: Partial<Record<MediaPlanUnitKey, number>> = {};
  if (d.hasDefaultMix) {
    for (const [k, v] of Object.entries(d.defaultUnitMix)) {
      const n = parseNum(v || '');
      if (n !== null && n > 0) mix[k as MediaPlanUnitKey] = n;
    }
  }
  const minBudget = parseNum(d.minimumBudgetUsd);
  return {
    ...(d.id ? { id: d.id } : {}),
    name: d.name.trim(),
    scope: d.scope,
    ...(d.scope === 'category' ? { categoryKey: d.categoryKey } : {}),
    ...(d.scope === 'agency' ? { agencyKey: d.agencyKey } : {}),
    ...(d.scope === 'advertiser' ? { advertiserKey: d.advertiserKey } : {}),
    status: d.status,
    effectiveFrom: d.effectiveFrom,
    ...(d.effectiveTo ? { effectiveTo: d.effectiveTo } : {}),
    units,
    upcharges,
    ...(d.hasAddedValue
      ? {
          addedValue: {
            bonusPct: (parseNum(d.bonusPct) || 0) / 100,
            bonusUnits: [...d.bonusUnits],
            ...(d.customItems.length
              ? {
                  customItems: d.customItems.map((c) => ({
                    minBudgetUsd: parseNum(c.minBudgetUsd) || 0,
                    label: c.label.trim(),
                    ...(parseNum(c.valueUsd) !== null ? { valueUsd: parseNum(c.valueUsd)! } : {}),
                  })),
                }
              : {}),
            ...(d.addedValueNotes.trim() ? { notes: d.addedValueNotes.trim() } : {}),
          },
        }
      : {}),
    ...(d.hasDefaultMix && Object.keys(mix).length ? { defaultUnitMix: mix } : {}),
    ...(minBudget !== null && minBudget > 0 ? { minimums: { budgetUsd: minBudget } } : {}),
    ...(d.notes.trim() ? { notes: d.notes.trim() } : {}),
  };
}

export const SCOPE_LABELS: Record<RateCardScope, string> = {
  default: 'Default',
  category: 'Category',
  agency: 'Agency',
  advertiser: 'Advertiser',
};

export const CARD_STATUS_LABELS: Record<RateCardStatus, string> = {
  draft: 'Draft',
  active: 'Active',
  archived: 'Archived',
};

export const LAYER_LABELS: Record<TargetingLayerKey, string> = {
  demo: 'Demo',
  'culture-stack': 'Culture Stack',
  geo: 'Geography',
  daypart: 'Daypart',
  competitive: 'Competitive conquesting',
};
